import log from 'loglevel';

// Configure the logger
// Set default level based on environment
if (import.meta.env.DEV) {
  log.setLevel('debug');
} else {
  log.setLevel('info');  // Allow info level in production for remote debugging
}

// Allow runtime level change via localStorage
const storedLevel = localStorage.getItem('logLevel');
if (storedLevel) {
  log.setLevel(storedLevel as log.LogLevelDesc);
}

// Remote logging configuration
interface LogEntry {
  level: string;
  message: string;
  timestamp: number;
}

const logBuffer: LogEntry[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 2000; // Flush logs every 2 seconds
const MAX_BUFFER_SIZE = 20; // Or when buffer reaches this size

// Determine API URL based on environment
const getApiUrl = () => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_URL || 'http://localhost:5200';
  }
  return `https://${window.location.hostname}`;
};

const flushLogs = async () => {
  if (logBuffer.length === 0) return;
  
  const entries = [...logBuffer];
  logBuffer.length = 0;

  try {
    await fetch(`${getApiUrl()}/api/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ entries }),
    });
  } catch {
    // Silently fail - don't log errors about logging to avoid infinite loops
  }
};

const scheduleFlush = () => {
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushLogs();
  }, FLUSH_INTERVAL);
};

const addToBuffer = (level: string, message: string) => {
  logBuffer.push({
    level,
    message,
    timestamp: Date.now(),
  });

  if (logBuffer.length >= MAX_BUFFER_SIZE) {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
      flushTimeout = null;
    }
    flushLogs();
  } else {
    scheduleFlush();
  }
};

// Wrap the original log methods to also send to backend
const originalFactory = log.methodFactory;
log.methodFactory = function (methodName, logLevel, loggerName) {
  const rawMethod = originalFactory(methodName, logLevel, loggerName);

  return function (this: log.Logger, ...args: unknown[]) {
    // Call original console method
    rawMethod.apply(this, args);

    // Also buffer for remote logging (only info, warn, error levels to reduce noise)
    if (['info', 'warn', 'error'].includes(methodName)) {
      const message = args
        .map((arg) =>
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        )
        .join(' ');
      addToBuffer(methodName, message);
    }
  };
};

// Rebuild methods with new factory
log.setLevel(log.getLevel());

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushLogs();
    }
  });
  
  window.addEventListener('pagehide', () => {
    flushLogs();
  });
}

// Export logger instance
export const logger = log;

// Helper to change log level at runtime
export const setLogLevel = (level: log.LogLevelDesc) => {
  log.setLevel(level);
  localStorage.setItem('logLevel', level.toString());
};

// Force flush logs (useful before navigation)
export const flushRemoteLogs = flushLogs;

export default logger;
